import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { authService } from '@/shared/lib/auth';
import { useAuth } from '@/shared/hooks/useAuth';
import { MfaApiService } from '@/features/mfa/api/mfaApi';
import { MfaCodeInput } from '@/features/mfa/components/MfaCodeInput';

const MfaChallengePage: React.FC = () => {
  const { t } = useTranslation('mfa');
  const navigate = useNavigate();
  const { finalizeAfterMfa } = useAuth();

  const challenge = useMemo(() => authService.getMfaChallenge(), []);

  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!challenge) {
      navigate('/login', { replace: true });
    }
  }, [challenge, navigate]);

  if (!challenge) return null;

  const submit = async (codeToSubmit: string) => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const auth = await MfaApiService.verify(challenge.mfaSessionToken, codeToSubmit);
      authService.completeLogin(auth);
      finalizeAfterMfa(auth.user);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.error?.message ??
        err?.message ??
        t('challengePage.invalidCodeRetry');
      setError(message);
      setCode('');
      setRecoveryCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl p-8 border border-white/10 bg-[#20263a]/95">
        <div className="flex flex-col items-center mb-6 text-center">
          <Shield className="w-12 h-12 text-cyan-400 mb-3" />
          <h1 className="text-xl font-semibold text-white">{t('challengePage.title')}</h1>
          <p className="text-sm text-gray-400 mt-1">
            {useRecovery
              ? t('challengePage.recoveryPrompt')
              : t('challengePage.codePrompt')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4 bg-red-500/10 border-red-500/30 text-red-400">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!useRecovery ? (
          <div className="flex flex-col items-center space-y-4">
            <MfaCodeInput
              value={code}
              onChange={setCode}
              onComplete={submit}
              disabled={submitting}
              autoFocus
            />
            <Button
              className="w-full mt-2"
              disabled={code.length !== 6 || submitting}
              onClick={() => submit(code)}
            >
              {submitting ? t('challengePage.verifying') : t('challengePage.verifyButton')}
            </Button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (recoveryCode.trim()) submit(recoveryCode.trim());
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="recovery" className="text-gray-300">{t('challengePage.recoveryCodeLabel')}</Label>
              <Input
                id="recovery"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder={t('challengePage.recoveryCodePlaceholder')}
                disabled={submitting}
                autoFocus
                className="font-mono uppercase tracking-wider"
              />
            </div>
            <Button type="submit" className="w-full" disabled={!recoveryCode.trim() || submitting}>
              {submitting ? t('challengePage.verifying') : t('challengePage.useRecoveryCodeButton')}
            </Button>
          </form>
        )}

        <div className="mt-6 pt-4 border-t border-white/10 flex flex-col space-y-2 text-center">
          <button
            type="button"
            className="text-sm text-cyan-400 hover:text-cyan-300 inline-flex items-center justify-center gap-1"
            onClick={() => {
              setUseRecovery((v) => !v);
              setError(null);
            }}
          >
            <KeyRound className="w-3 h-3" />
            {useRecovery ? t('challengePage.backToAuthenticatorButton') : t('challengePage.noAuthenticatorAccessButton')}
          </button>
          <button
            type="button"
            className="text-sm text-gray-400 hover:text-gray-200 inline-flex items-center justify-center gap-1"
            onClick={() => {
              authService.clearMfaChallenge();
              navigate('/login', { replace: true });
            }}
          >
            <ArrowLeft className="w-3 h-3" />
            {t('challengePage.cancelAndBackButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MfaChallengePage;
